import { inferRouteObject } from 'react-router-typed-object';

import { Navigate } from '@/js/libs/router';
import { and, not, PrivateRoute, Roles } from '@/js/libs/use-auth';

import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import { DEV_MODE } from 'src/constants.ts';
import { validateApprovalStatus, validateDigitalOperationStatus } from 'src/scenes/DigitalOperations/helpers.ts';
import DigitalOperations from 'src/scenes/DigitalOperations/index.ts';
import { ApprovalStatus, DigitalOperationStatus, ScreenshotManagementScope } from 'src/scenes/DigitalOperations/types.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

export const digitalOperationsRouteObject = inferRouteObject({
  path: 'digital-operations',
  element: <PrivateRoute roles={Roles.MANAGE_DIGITAL_PRODUCTS} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: DigitalOperations.Helmet,
      children: [
        {
          path: 'approval',
          element: <PrivateRoute roles={and(Roles.OPS_PUSH, not(Roles.USER))} noAccessComponent={Status.NotFound} />,
          children: [
            { index, element: <Navigate to={`./${ApprovalStatus.PENDING_APPROVAL}`} replace /> },
            {
              Component: DigitalOperations.Approval.Layout,
              children: [
                {
                  path: ':status',
                  element: <RouteParamsValidator validate={validateApprovalStatus} invalidStateElement={<Status.NotFound />} />,
                  children: [
                    {
                      index,
                      Component: DigitalOperations.Approval.CreativesList,
                    },
                  ],
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    {
                      path: 'crawler-articles',
                      Component: DigitalOperations.Approval.CreativeDraftsPage,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'approval-v2',
          element: DEV_MODE ? (
            <PrivateRoute roles={and(Roles.OPS_PUSH, not(Roles.USER))} noAccessComponent={Status.NotFound} />
          ) : (
            <Status.NotFound />
          ),
          children: [
            { index, element: <Navigate to={`./${ApprovalStatus.PENDING_APPROVAL}`} replace /> },
            {
              Component: DigitalOperations.ApprovalV2.Layout,
              children: [
                {
                  path: ':status',
                  element: <RouteParamsValidator validate={validateApprovalStatus} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, Component: DigitalOperations.ApprovalV2.CreativesList }],
                },
                {
                  element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
                  children: [
                    {
                      path: 'crawler-articles',
                      Component: DigitalOperations.ApprovalV2.CrawlerArticles,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'campaign-management',
          children: [
            { index, element: <Navigate to={`./${DigitalOperationStatus.READY_TO_PUSH}`} replace /> },
            {
              path: ':status',
              element: <RouteParamsValidator validate={validateDigitalOperationStatus} invalidStateElement={<Status.NotFound />} />,
              children: [
                {
                  element: <PrivateRoute roles={Roles.OPS_PUSH} noAccessComponent={Status.NotFound} />,
                  children: [
                    {
                      index,
                      Component: DigitalOperations.CampaignManagement,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'campaign-management2',
          element: DEV_MODE ? <PrivateRoute roles={Roles.OPS_PUSH} noAccessComponent={Status.NotFound} /> : <Status.NotFound />,
          children: [
            { index, Component: DigitalOperations.CampaignManagement2 },
            { path: '*', element: <Navigate to='/digital-operations/campaign-management2' replace /> },
          ],
        },
        {
          path: 'screenshot-management',
          element: <PrivateRoute roles={Roles.OPS_SCREENSHOTS} noAccessComponent={Status.NotFound} />,
          children: [
            { index, element: <Navigate to={`./${ScreenshotManagementScope.CAMPAIGNS}`} replace /> },
            {
              Component: DigitalOperations.ScreenshotManagement.Layout,
              children: [
                {
                  path: ScreenshotManagementScope.CAMPAIGNS,
                  element: <DigitalOperations.ScreenshotManagement.Campaigns statuses={['NEW', 'DECLINED']} />,
                },
                {
                  path: ScreenshotManagementScope.APPROVALS,
                  element: <DigitalOperations.ScreenshotManagement.Approvals statuses={['PENDING']} />,
                },
                {
                  path: ScreenshotManagementScope.DONE,
                  element: <DigitalOperations.ScreenshotManagement.Done statuses={['APPROVED']} />,
                },
                {
                  path: ScreenshotManagementScope.HIDDEN,
                  element: <DigitalOperations.ScreenshotManagement.Hidden statuses={['HIDDEN']} />,
                },
              ],
            },
          ],
        },
        {
          path: 'quality-assurance',
          element: <PrivateRoute roles={Roles.OPS_CHECKS} noAccessComponent={Status.NotFound} />,
          children: [
            { index, Component: DigitalOperations.QualityAssurance },
            { path: '*', element: <Navigate to='/digital-operations/quality-assurance' replace /> },
          ],
        },
      ],
    },
  ],
});
